import React, { useState } from 'react';
import { useClientsPageOptimized } from '@/hooks/useClientsPageOptimized';
import { useAdminClinicaPersistente } from '@/hooks/useAdminClinicaPersistente';
import { ContactsLoadingState } from './ContactsLoadingState';
import { ContactsTableSkeleton } from './ContactsTableSkeleton';
import { ContactsEmptyState } from './ContactsEmptyState';
import { ContactsFilters } from './ContactsFilters';
import { ContactsTable } from './ContactsTable';
import { ClientsPageHeader } from './ClientsPageHeader';
import { ClientsActionsBar } from './ClientsActionsBar';
import { ActiveFiltersDisplay } from './ActiveFiltersDisplay';
import { AdminClinicSelector } from '@/components/admin/AdminClinicSelector';
import { LeadModal } from '@/components/kanban/LeadModal';

/**
 * Página principal de clientes/contatos da clínica - VERSÃO OTIMIZADA
 * 
 * MELHORIAS IMPLEMENTADAS:
 * - Paginação infinita com carregamento sob demanda
 * - Busca server-side com debounce automático
 * - Filtros aplicados diretamente no banco de dados
 * - Performance otimizada para grandes volumes de dados
 * - Busca que encontra contatos mesmo não carregados inicialmente
 * - Estados de carregamento e erro aprimorados
 * - Compatibilidade completa com modo admin
 */

export const ClientsPage = () => {
  // Hook para gerenciar seleção de clínica admin
  const {
    clinicaSelecionada,
    selecionarClinica,
    isLoading: adminLoading,
    isAdmin
  } = useAdminClinicaPersistente();

  // Hook principal otimizado para gerenciar a página
  const {
    loading,
    isError,
    error,
    tags,
    etapas,
    availableAds,
    availablePlatforms,
    searchQuery,
    setSearchQuery,
    isFilterOpen,
    setIsFilterOpen,
    displayedLeads,
    selectedLeadIds,
    filters,
    totalLeads,
    displayedLeadsCount,
    hasActiveFilters,
    // Estados para carregamento infinito
    hasMoreLeads,
    remainingLeadsCount,
    isLoadingMore,
    handleLoadMore,
    // Estados de exportação
    isExporting,
    // Handlers otimizados
    handleSort,
    handleFilterChange,
    clearFilters,
    handleSelectLead,
    handleSelectAll,
    handleDeleteSelected,
    handleBulkStatusUpdate,
    handleExportContacts,
    handleSaveLead,
    handleDeleteLead,
    handleOpenChat, // Função para abrir chat
    refreshData
  } = useClientsPageOptimized({
    adminMode: isAdmin,
    targetClinicaId: clinicaSelecionada?.id || null
  });

  // Estados locais para modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState(null);

  // Função para abrir modal de adicionar lead
  const handleOpenAddModal = () => {
    setSelectedLeadForEdit(null);
    setIsModalOpen(true);
  };

  // Função para abrir modal de editar lead
  const handleOpenEditModal = (lead: any) => {
    setSelectedLeadForEdit(lead);
    setIsModalOpen(true);
  };

  // Função para fechar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLeadForEdit(null);
  };

  // Função para salvar lead (criar ou atualizar)
  const handleSaveLeadData = async (leadData: any) => {
    await handleSaveLead(leadData);
    handleCloseModal();
  };

  // Verificar se há seleções
  const hasSelection = selectedLeadIds.length > 0;
  const allSelected = selectedLeadIds.length === displayedLeads.length && displayedLeads.length > 0;

  // Estado de carregamento admin (ainda precisa bloquear tudo)
  if (adminLoading) {
    return <ContactsLoadingState />;
  }

  // Estado especial para admin sem clínica selecionada
  if (isAdmin && !clinicaSelecionada) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col gap-6">
        {/* Seletor de Clínica para Admin */}
        <div className="shrink-0 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 shadow-sm">
          <AdminClinicSelector
            clinicaSelecionada={clinicaSelecionada}
            onClinicaSelected={selecionarClinica}
            showStats={true}
          />
        </div>

        {/* Estado vazio para admin sem seleção */}
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Selecione uma Clínica
            </h3>
            <p className="text-gray-600">
              Escolha uma clínica no seletor acima para visualizar e gerenciar seus contatos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-6">
      {/* Seletor de Clínica para Admin */}
      {isAdmin && (
        <div className="shrink-0 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 shadow-sm">
          <AdminClinicSelector
            clinicaSelecionada={clinicaSelecionada}
            onClinicaSelected={selecionarClinica}
            showStats={true}
          />

          {/* Badge indicando clínica ativa */}
          {clinicaSelecionada && (
            <div className="mt-3 flex items-center gap-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                Visualizando: {clinicaSelecionada.nome}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cabeçalho da página */}
      <div className="shrink-0">
        <ClientsPageHeader onAddLead={handleOpenAddModal} />
      </div>

      {/* Barra de ações quando há contatos selecionados */}
      {hasSelection && (
        <div className="shrink-0">
          <ClientsActionsBar
            selectedCount={selectedLeadIds.length}
            onExport={handleExportContacts}
            onDelete={handleDeleteSelected}
            onStatusUpdate={handleBulkStatusUpdate}
            isExporting={isExporting}
          />
        </div>
      )}

      {/* Filtros e busca otimizados */}
      <div className="shrink-0 space-y-4">
        <ContactsFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={handleFilterChange}
          tags={tags}
          etapas={etapas}
          totalContacts={totalLeads}
          isOpen={isFilterOpen}
          onToggle={() => setIsFilterOpen(!isFilterOpen)}
          onExport={handleExportContacts}
          isExporting={isExporting}
          hasActiveFilters={hasActiveFilters}
          selectedCount={selectedLeadIds.length}
          availableAds={availableAds}
          availablePlatforms={availablePlatforms}
        />

        {/* Exibir filtros ativos */}
        {hasActiveFilters && (
          <ActiveFiltersDisplay
            filters={filters}
            onClearFilters={clearFilters}
            filteredCount={displayedLeadsCount}
            totalCount={totalLeads}
          />
        )}
      </div>

      {/* Estado de carregamento inicial - Skeleton apenas na área da tabela */}
      {loading ? (
        <div className="crm-scroll min-h-0 flex-1">
          <ContactsTableSkeleton />
        </div>
      ) : totalLeads === 0 ? (
        <div className="min-h-0 flex-1">
          <ContactsEmptyState
            searchQuery={searchQuery}
            onAddLead={handleOpenAddModal}
            hasFilters={filters.hasActiveFilters}
            onClearFilters={clearFilters}
          />
        </div>
      ) : (
        <div className="crm-scroll crm-scroll-fade min-h-0 flex-1">
          {/* Tabela de contatos com carregamento infinito */}
          <ContactsTable
            leads={displayedLeads}
            selectedLeads={selectedLeadIds}
            onLeadSelect={handleSelectLead}
            onSelectAll={handleSelectAll}
            isAllSelected={allSelected}
            onViewLead={handleOpenEditModal}
            onEditLead={handleOpenEditModal}
            onDeleteLead={handleDeleteLead}
            onOpenChat={handleOpenChat} // Passa função para abrir chat
            hasMoreLeads={hasMoreLeads}
            remainingLeadsCount={remainingLeadsCount}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
            totalLeads={totalLeads}
            displayedLeadsCount={displayedLeadsCount}
            isError={isError}
            error={error}
          />
        </div>
      )}

      {/* Modal para adicionar/editar lead */}
      <LeadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        lead={selectedLeadForEdit}
        onSave={handleSaveLeadData}
        etapas={etapas}
      />
    </div>
  );
};
